import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'

import background from 'assets/images/signin_background.jpg'

/* Values with no Miranda equivalent: the panel darks sit below
   --m-color-neutral-100, and the shadows are softer than the --m-elevation ramp. */
const PANEL_DARK = '#161e24'
const PANEL_DARK_TOP = '#2c3843'
const DEMO_DARK_TOP = '#28333c'
const DEMO_DARK_BOTTOM = '#151c22'
const SHADOW = '0 1px 2px rgba(32, 41, 49, 0.05), 0 12px 28px -14px rgba(32, 41, 49, 0.28)'
const SHADOW_LIFT = '0 2px 4px rgba(32, 41, 49, 0.06), 0 22px 44px -18px rgba(32, 41, 49, 0.38)'

const BRAND_GRADIENT = `linear-gradient(
  45deg,
  rgb(var(--m-color-brand-accent-1)) 0%,
  rgb(var(--m-color-brand-accent-2)) 100%
)`

const EASE_OUT = 'cubic-bezier(0.2, 0.7, 0.3, 1)'

const rise = keyframes`
  to {
    opacity: 1;
    transform: none;
  }
`

const riseIn = (delay: string) => css`
  opacity: 0;
  transform: translateY(12px);
  animation: ${rise} 0.6s ${EASE_OUT} ${delay} forwards;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    animation: none;
  }
`

/* --- shell ---------------------------------------------------------------- */

export const StyledShell = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);

  @media screen and (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`

/* --- brand panel ---------------------------------------------------------- */

export const StyledBrandPanel = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: var(--m-spacing-10) var(--m-spacing-12) var(--m-spacing-14);
  background-color: ${PANEL_DARK};
  color: rgb(var(--m-color-text-inverted));
  overflow: hidden;
  /* Scopes the pseudo-elements' negative z-indexes to this panel. */
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background-image: url(${background});
    background-size: cover;
    background-position: 58% 42%;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
        180deg,
        rgba(15, 21, 26, 0.72) 0%,
        rgba(15, 21, 26, 0.34) 38%,
        rgba(15, 21, 26, 0.9) 100%
      ),
      linear-gradient(90deg, rgba(15, 21, 26, 0.55) 0%, rgba(15, 21, 26, 0) 55%);
  }

  @media screen and (max-width: 980px) {
    min-height: 280px;
    padding: var(--m-spacing-8) var(--m-spacing-8) 36px;

    &::before {
      background-position: 58% 38%;
    }
  }
`

export const StyledBrandRail = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 6px;
  z-index: 1;
  background: ${BRAND_GRADIENT};

  @media screen and (max-width: 980px) {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    height: 5px;
  }
`

export const StyledLogo = styled.div`
  ${riseIn('0.05s')}

  svg {
    width: 172px;
    height: auto;
    display: block;
  }
`

export const StyledBrandCopy = styled.div`
  max-width: min(500px, 100%);
  ${riseIn('0.13s')}
`

export const StyledEyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 var(--m-spacing-4);
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  white-space: nowrap;
  color: #9ff4c6;

  &::before {
    content: '';
    width: 26px;
    height: 2px;
    border-radius: 2px;
    background: ${BRAND_GRADIENT};
  }
`

export const StyledBrandHeadline = styled.h1`
  margin: 0;
  font-family: var(--m-font-family-title);
  font-size: clamp(34px, 3.4vw, 52px);
  font-weight: var(--m-font-weight-bold);
  line-height: 104%;
  letter-spacing: -0.02em;
  text-wrap: balance;

  @media screen and (max-width: 980px) {
    font-size: clamp(28px, 6vw, 38px);
  }
`

/* --- auth panel ----------------------------------------------------------- */

export const StyledAuthPanel = styled.main`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--m-spacing-14) clamp(32px, 5vw, 84px);
  background-color: rgb(var(--m-color-background-primary));

  @media screen and (max-width: 980px) {
    padding: var(--m-spacing-10) var(--m-spacing-8) var(--m-spacing-12);
  }
`

export const StyledAuthInner = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
`

export const StyledAuthHeading = styled.h2`
  margin: 0 0 28px;
  font-family: var(--m-font-family-title);
  font-size: 30px;
  font-weight: var(--m-font-weight-bold);
  line-height: 118%;
  letter-spacing: -0.015em;
  color: rgb(var(--m-color-text-primary));
  ${riseIn('0.13s')}
`

/* --- failed sign-in notice ------------------------------------------------ */

export const StyledAuthError = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: var(--m-spacing-1) 14px;
  margin-bottom: 22px;
  padding: var(--m-spacing-4) 18px var(--m-spacing-5);
  background-color: rgb(var(--m-color-warning-20));
  border: var(--m-border-thin) solid rgb(var(--m-color-warning-60));
  border-radius: var(--m-border-radius-l);
  font-family: var(--m-font-family-default);
  ${riseIn('0.17s')}
`

export const StyledAuthErrorIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  color: rgb(var(--m-color-warning-100));
`

export const StyledAuthErrorTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: var(--m-font-weight-bold);
  line-height: 20px;
  color: rgb(var(--m-color-text-primary));
`

export const StyledAuthErrorBody = styled.p`
  grid-column: 2;
  margin: 0;
  font-size: 13px;
  line-height: 150%;
  color: rgb(var(--m-color-text-secondary));
`

export const StyledAuthErrorAction = styled.button`
  font: inherit;
  background: none;
  border: 0;
  padding: 0 0 1px;
  border-bottom: var(--m-border-thin) solid currentcolor;
  color: rgb(var(--m-color-text-primary));
  font-weight: var(--m-font-weight-bold);
  cursor: pointer;
`

export const StyledAppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

/* --- product sign-in cards ------------------------------------------------ */

export const StyledAppCard = styled(Link)<{ $delay: string }>`
  display: grid;
  grid-template-columns: 56px 1fr 20px;
  align-items: center;
  gap: 18px;
  padding: var(--m-spacing-5) 22px;
  background-color: #fff;
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-l);
  box-shadow: ${SHADOW};
  text-decoration: none;
  transition:
    transform 0.22s ${EASE_OUT},
    box-shadow 0.22s ease,
    border-color 0.22s ease;
  ${({ $delay }) => riseIn($delay)}

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${SHADOW_LIFT};
    border-color: rgb(var(--m-color-border-input));
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:active {
      transform: none;
    }
  }

  @media screen and (max-width: 460px) {
    grid-template-columns: 48px 1fr;
    gap: 14px;
    padding: 18px;
    align-items: start;
  }
`

export const StyledTile = styled.span`
  width: 56px;
  height: 56px;
  border-radius: var(--m-border-radius-l);
  display: grid;
  place-items: center;
  background: linear-gradient(160deg, ${PANEL_DARK_TOP} 0%, ${PANEL_DARK} 100%);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.14),
    0 6px 14px -6px rgba(32, 41, 49, 0.55);

  img,
  svg {
    width: 30px;
    height: 30px;
    display: block;
    object-fit: contain;
  }

  @media screen and (max-width: 460px) {
    width: 48px;
    height: 48px;
    border-radius: 14px;

    img,
    svg {
      width: 26px;
      height: 26px;
    }
  }
`

/* Height-constrained, not width-constrained, so the two wordmarks align on cap
   height despite their different aspect ratios. */
export const StyledAppName = styled.span`
  display: block;
  height: 19px;
  margin-bottom: var(--m-spacing-2);
  color: rgb(var(--m-color-text-primary));

  svg {
    height: 100%;
    width: auto;
    display: block;
  }
`

export const StyledAppDescription = styled.p`
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: 13.5px;
  line-height: 145%;
  color: rgb(var(--m-color-text-placeholder));
`

export const StyledGoIcon = styled.span`
  display: flex;
  color: rgb(var(--m-color-border-input));
  transition:
    transform 0.22s ${EASE_OUT},
    color 0.22s ease;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }

  ${StyledAppCard}:hover & {
    transform: translateX(4px);
    color: rgb(var(--m-color-text-primary));
  }

  @media screen and (max-width: 460px) {
    ${StyledAppCard} & {
      display: none;
    }
  }
`

/* --- basic auth ----------------------------------------------------------- */

export const StyledSplit = styled.p`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 30px 0 18px;
  font-family: var(--m-font-family-title);
  font-size: 11px;
  font-weight: var(--m-font-weight-medium);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
  color: rgb(var(--m-color-text-placeholder));
  ${riseIn('0.37s')}

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: rgb(var(--m-color-divider));
  }
`

export const StyledBasicRow = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--m-spacing-4);
  padding: 15px var(--m-spacing-5);
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-m);
  background-color: rgb(var(--m-color-background-secondary));
  text-decoration: none;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease;
  ${riseIn('0.37s')}

  &:hover {
    background-color: rgb(var(--m-color-background-tertiary));
    border-color: rgb(var(--m-color-border-input));
  }

  ${StyledGoIcon} {
    color: rgb(var(--m-color-text-placeholder));
  }

  &:hover ${StyledGoIcon} {
    transform: translateX(3px);
    color: rgb(var(--m-color-text-secondary));
  }
`

export const StyledBasicTitle = styled.p`
  margin: 0 0 var(--m-spacing-0-5);
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-text-primary));
`

export const StyledBasicSubtitle = styled.p`
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: 12.5px;
  color: rgb(var(--m-color-text-placeholder));
`

/* --- demo ----------------------------------------------------------------- */

export const StyledDemoCard = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--m-spacing-5);
  margin-top: 22px;
  padding: 22px;
  border-radius: var(--m-border-radius-l);
  background: linear-gradient(150deg, ${DEMO_DARK_TOP} 0%, ${DEMO_DARK_BOTTOM} 100%);
  color: rgb(var(--m-color-text-inverted));
  text-decoration: none;
  overflow: hidden;
  transition:
    transform 0.22s ${EASE_OUT},
    box-shadow 0.22s ease;
  ${riseIn('0.37s')}

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 3px;
    background: ${BRAND_GRADIENT};
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 24px 46px -20px rgba(16, 22, 28, 0.65);
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:active {
      transform: none;
    }
  }

  @media screen and (max-width: 460px) {
    flex-direction: column;
    align-items: stretch;
    gap: var(--m-spacing-4);
    padding: var(--m-spacing-5);
  }
`

export const StyledDemoTitle = styled.p`
  margin: 0 0 5px;
  font-family: var(--m-font-family-title);
  font-size: 15.5px;
  font-weight: var(--m-font-weight-bold);
  line-height: 130%;
`

export const StyledDemoSubtitle = styled.p`
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: 12.5px;
  line-height: 150%;
  color: #a7b9c6;
`

export const StyledDemoCta = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--m-spacing-2);
  padding: 10px 15px;
  border-radius: 11px;
  background: ${BRAND_GRADIENT};
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  white-space: nowrap;
  color: #12191f;

  svg {
    width: 15px;
    height: 15px;
    transition: transform 0.22s ${EASE_OUT};
  }

  ${StyledDemoCard}:hover & svg {
    transform: translateX(3px);
  }

  @media screen and (max-width: 460px) {
    justify-content: center;
  }
`

/* --- footer --------------------------------------------------------------- */

export const StyledFooter = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--m-spacing-2) var(--m-spacing-5);
  margin-top: 36px;
  font-family: var(--m-font-family-default);
  font-size: 12.5px;
  color: rgb(var(--m-color-text-placeholder));
  ${riseIn('0.37s')}
`

export const StyledFooterDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: var(--m-border-radius-circle);
  background-color: rgb(var(--m-color-border-input));
`

export const StyledLinkButton = styled.button`
  font: inherit;
  background: none;
  border: 0;
  padding: 0 0 1px;
  border-bottom: var(--m-border-thin) solid rgb(var(--m-color-border-input));
  color: rgb(var(--m-color-text-secondary));
  cursor: pointer;

  &:hover {
    color: rgb(var(--m-color-text-primary));
    border-color: rgb(var(--m-color-text-primary));
  }
`
